"use client";

import type { GalleryZoomState } from "../../blocks/image-gallery/image-gallery.types";
import { Image } from "../../elements/image/image";
import { cn } from "../../lib/utils";

export interface GalleryImageProps {
  /** Image source URL */
  src: string;

  /** Accessible alt text */
  alt: string;

  /** Zoom state for cursor behavior */
  zoomed?: GalleryZoomState;

  /** Object fit mode - cover fills container, contain preserves aspect ratio */
  objectFit?: "cover" | "contain";

  /** Additional CSS classes */
  className?: string;

  /** Rendered width hint for the browser's candidate selection */
  sizes?: string;

  /** Loading callback */
  onLoad?: () => void;

  /** Click handler */
  onClick?: () => void;

  draggable?: boolean;
}

/**
 * GalleryImage - Simple image component that fills its container
 *
 * Figma specs:
 * - Desktop: 650×650px container with image fill (cover)
 * - Mobile: 375px height, full width with image fill (cover)
 * - Uses absolute positioning to fill parent container
 */
export function GalleryImage(props: Readonly<GalleryImageProps>) {
  const objectFit = props.objectFit ?? "cover";

  const cursorClass = cn(
    props.zoomed === "Yes" && "cursor-zoom-out",
    props.zoomed === "No" && "cursor-zoom-in"
  );

  return (
    <Image
      alt={props.alt}
      aspectRatio="1:1"
      className={cn(
        "absolute inset-0 h-full w-full bg-white",
        objectFit === "cover" ? "object-cover" : "object-contain",
        cursorClass,
        props.className
      )}
      draggable={props.draggable}
      onClick={props.onClick}
      onLoad={props.onLoad}
      sizes={props.sizes}
      src={props.src}
    />
  );
}
